import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { routes } from '../../../../config/routes';
import { cn } from '../../../../utils/tailwind';

type NavigationType = {
    inverse: boolean;
};

export const Navigation: React.FC<NavigationType> = observer(({ inverse }) => {
    const { t } = useTranslation();
    const router = useRouter();
    const items = [
        {
            href: routes.web.index,
            label: t('pages.layout.header.home'),
            active: router.pathname === routes.web.index,
        },
        {
            href: routes.web.pricing,
            label: t('pages.layout.header.pricing'),
            active: router.pathname === routes.web.pricing,
        },
        {
            href: routes.web.compoundInterest,
            label: t('pages.layout.header.calculator'),
            active: router.pathname === routes.web.compoundInterest || router.pathname === '/compound-interest',
        },
        {
            href: routes.web.faq,
            label: t('pages.layout.header.faq'),
            active: router.pathname === routes.web.faq,
        },
        {
            href: routes.web.contact,
            label: t('pages.layout.header.contact'),
            active: router.pathname === routes.web.contact,
        },
    ];

    return (
        <nav
            className={cn(
                'hidden h-[5rem] items-center gap-[0.4rem] rounded-full border p-[0.4rem] min-[1100px]:flex',
                inverse ? 'border-white/25 bg-transparent' : 'border-[#d9cdc5] bg-transparent',
            )}
        >
            {items.map(({ href, label, active }) => (
                <Link key={href} href={href}>
                    <a
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                            'relative isolate inline-flex h-full items-center justify-center whitespace-nowrap rounded-full px-[2rem] text-[1.4rem] font-bold leading-[1.4rem] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary',
                            active && 'text-white',
                            !active &&
                                (inverse ? 'text-white hover:bg-white/20' : 'text-[#72645a] hover:bg-[#d9cdc5]/40'),
                        )}
                    >
                        {active && (
                            <span
                                data-web-nav-pill
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-0 -z-[1] rounded-full bg-brand-secondary shadow-sm"
                            />
                        )}
                        {label}
                    </a>
                </Link>
            ))}
        </nav>
    );
});
